import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { parseMemberAnalytics, type AnalyticsRange, type MemberAnalytics } from "@/lib/analytics";

const KEY = "member-analytics";

export async function fetchMemberAnalytics(range: AnalyticsRange): Promise<MemberAnalytics> {
  const { data, error } = await supabase.rpc("get_member_analytics", {
    p_start: range.start.toISOString(),
    p_end: range.end.toISOString(),
  });
  if (error) throw new Error(error.message);
  return parseMemberAnalytics(data);
}

export function useMemberAnalytics(range: AnalyticsRange | null) {
  return useQuery<MemberAnalytics, Error>({
    queryKey: [KEY, range?.start.toISOString(), range?.end.toISOString()],
    queryFn: () => fetchMemberAnalytics(range as AnalyticsRange),
    enabled: range !== null,
    staleTime: 60_000,
  });
}

/** Mensagem amigavel para os erros estaveis da RPC request_audience_snapshot. */
export function snapshotErrorMessage(message: string): string {
  if (message.includes("too_soon")) return "Você já pediu uma atualização há menos de 1 minuto. Aguarde um pouco.";
  return "Não foi possível pedir a atualização agora. Tente de novo em instantes.";
}

/** Botao "Atualizar": pede ao bot um snapshot de inscritos e recarrega em seguida. */
export function useRequestAudienceSnapshot() {
  const queryClient = useQueryClient();
  return useMutation<void, Error>({
    mutationFn: async () => {
      const { error } = await supabase.rpc("request_audience_snapshot");
      if (error) throw new Error(snapshotErrorMessage(error.message));
    },
    onSuccess: () => {
      // O bot atende em ate ~15s; recarrega duas vezes para pegar o snapshot novo.
      for (const delay of [8_000, 20_000]) {
        setTimeout(() => void queryClient.invalidateQueries({ queryKey: [KEY] }), delay);
      }
    },
  });
}
